import React from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { CirclePlay, ChevronRight, HelpCircle, Plus } from "lucide-react";
import { FireMmkIcon } from "../../assets/icon";
import { MAX_PERSONAL_MMK_CARDS } from "../../helpers/mmkPersonal";
import {
  mmkHeaderStyles,
  mmkHeaderTopRowStyles,
  mmkHeaderLeftStyles,
  mmkHeaderTitleBlockStyles,
  mmkTitleStyles,
  mmkSubtitleStyles,
  getMmkProgressStyles,
  mmkStartSessionButtonStyles,
  mmkQuizStartButtonStyles,
} from "./styles";

interface MmkHeaderProps {
  masteredCount: number;
  totalCount: number;
  toReviewCount: number;
  todayReviewCount: number;
  onStartClick: () => void;
  onAllPassedClick: () => void;
  onStartQuizClick: () => void;
  onAddPersonalCard?: () => void;
  addPersonalCardDisabled?: boolean;
}

export const MmkHeader: React.FC<MmkHeaderProps> = ({
  masteredCount,
  totalCount,
  toReviewCount,
  todayReviewCount,
  onStartClick,
  onAllPassedClick,
  onStartQuizClick,
  onAddPersonalCard,
  addPersonalCardDisabled = false,
}) => {
  const canStartQuiz = totalCount > 0;
  const addCardLimitTooltip = `You've reached the limit of ${MAX_PERSONAL_MMK_CARDS} personal cards. Delete one to add another.`;

  return (
    <Box sx={mmkHeaderStyles}>
      <Box sx={mmkHeaderTopRowStyles}>
        <Box sx={mmkHeaderLeftStyles}>
          <FireMmkIcon style={{ width: 36, height: 36, flexShrink: 0 }} />
          <Box sx={mmkHeaderTitleBlockStyles}>
            <Typography variant="h6" sx={mmkTitleStyles}>
              MMM Mastery
            </Typography>
            <Typography variant="body2" sx={mmkSubtitleStyles}>
              {masteredCount}/{totalCount} mastered
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 0.5, md: 1 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {onAddPersonalCard &&
            (addPersonalCardDisabled ? (
              <Tooltip title={addCardLimitTooltip} arrow enterTouchDelay={300}>
                <span
                  style={{
                    display: "inline-flex",
                    cursor: "not-allowed",
                  }}
                >
                  <Button
                    sx={mmkQuizStartButtonStyles(true)}
                    onClick={onAddPersonalCard}
                    disabled
                    startIcon={
                      <Plus size={18} color="#94A3B8" strokeWidth={2} />
                    }
                  >
                    Add Card
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                sx={mmkQuizStartButtonStyles(false)}
                onClick={onAddPersonalCard}
                startIcon={<Plus size={18} color="#14b8a6" strokeWidth={2} />}
              >
                Add Card
              </Button>
            ))}
          <Button
            sx={mmkQuizStartButtonStyles(!canStartQuiz)}
            onClick={canStartQuiz ? onStartQuizClick : undefined}
            disabled={!canStartQuiz}
            startIcon={
              <HelpCircle
                size={18}
                color={canStartQuiz ? "#14b8a6" : "#94A3B8"}
                strokeWidth={2}
              />
            }
          >
            Start Quiz
          </Button>
          <Button
            sx={mmkStartSessionButtonStyles(toReviewCount === 0)}
            onClick={toReviewCount === 0 ? onAllPassedClick : onStartClick}
            startIcon={
              <CirclePlay
                size={20}
                color={toReviewCount === 0 ? "#64748B" : "#14b8a6"}
                strokeWidth={2}
              />
            }
            endIcon={<ChevronRight size={20} color="#64748B" />}
          >
            Start Focus Session
          </Button>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={totalCount > 0 ? (masteredCount / totalCount) * 100 : 0}
        sx={getMmkProgressStyles()}
      />
      <Typography variant="body2" sx={mmkSubtitleStyles}>
        Today: {todayReviewCount} cards to review
      </Typography>
    </Box>
  );
};
